import { AnimatePresence, motion } from 'framer-motion';
import { lazy, Suspense } from 'react';
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import DonationBanner from '@components/donation/DonationBanner';
import ProgressSteps from '@components/ui/ProgressSteps';
import type { WizardStepId } from '@components/ui/ProgressSteps';
import { useUIStore } from '@stores/useUIStore';

const RoomSetupPanel = lazy(() => import('@features/room-setup/RoomSetupPanel'));
const ObjectsPanel = lazy(() => import('@features/objects/ObjectsPanel'));
const AnalysisPanel = lazy(() => import('@features/analysis/AnalysisPanel'));

/**
 * Sidebar — fixed 280px left column holding the wizard. It does two
 * things: (1) renders `ProgressSteps` at the top so the user knows
 * where they are and can jump between visited steps, and (2) swaps the
 * active step panel below with a horizontal slide + fade transition
 * driven by `AnimatePresence`.
 *
 * Panels read their own slices from the stores — the sidebar itself
 * only knows about `useUIStore.currentStep` + `highestStep`.
 */
export default function Sidebar() {
  const currentStep = useUIStore((s) => s.currentStep);
  const highestStep = useUIStore((s) => s.highestStep);
  const goToStep = useUIStore((s) => s.goToStep);
  const triggerDonation = useUIStore((s) => s.triggerDonationOnFirstAnalysis);
  const sidebarPct = useUIStore((s) => s.sidebarWidthPct);

  /* Show donation banner 2.5s after the user first sees Step 3. */
  useEffect(() => {
    if (currentStep === 3) triggerDonation();
  }, [currentStep, triggerDonation]);

  return (
    <aside
      aria-label="Wizard sidebar"
      style={{
        width: `${sidebarPct}%`,
        flexShrink: 0,
        height: '100%',
        minWidth: 280,
        background: 'var(--bg-panel)',
        overflowY: 'auto',
        overflowX: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Progress indicator — sticky at the top of the sidebar so it's
          always visible even when the active panel scrolls. */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          padding: 'var(--space-xl) var(--space-lg) var(--space-lg)',
          background: 'var(--bg-panel)',
          borderBottom: '1px solid var(--border-dim)',
          zIndex: 1,
        }}
      >
        <ProgressSteps
          currentStep={currentStep}
          highestStep={highestStep}
          onStepClick={goToStep}
        />
      </div>

      {/* Active panel — slides + fades between steps */}
      <div
        style={{
          flex: 1,
          padding: 'var(--space-xl)',
          minHeight: 0, // allow flex-basis auto to shrink past content
        }}
      >
        <AnimatePresence mode="wait" initial={false}>
          <PanelShell key={currentStep} stepId={currentStep}>
            {renderPanel(currentStep)}
          </PanelShell>
        </AnimatePresence>
      </div>

      {/* Donation banner — sticky at the bottom, appears after first analysis */}
      <DonationBanner />
    </aside>
  );
}

/* ─── Internal: animated wrapper around each step panel ──────────── */

interface PanelShellProps {
  stepId: WizardStepId;
  children: ReactNode;
}

function PanelShell({ children }: PanelShellProps) {
  return (
    <motion.div
      // Slide-in from the right + fade. 200 ms ease-out per panel-swap
      // spec in the wizard section of DESIGN.md.
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-lg)',
      }}
    >
      <Suspense fallback={null}>{children}</Suspense>
    </motion.div>
  );
}

/* ─── Panel dispatch ─────────────────────────────────────────────── */

function renderPanel(step: WizardStepId): ReactNode {
  switch (step) {
    case 1:
      return <RoomSetupPanel />;
    case 2:
      return <ObjectsPanel />;
    case 3:
      return <AnalysisPanel />;
  }
}
